import { createContext, useContext, useState, ReactNode, useMemo } from 'react';

type TopBarAction = () => void;

interface MainContainerContextType {
    topBarAction?: TopBarAction | null;
    topBarActionTitle?: string;
    topBarIcon?: ReactNode;
    disabledAction?: boolean;
    disabledActionTooltip?: string;
    setTopBarAction: (action: TopBarAction | null) => void;
    setTopBarActionTitle: (title: string | undefined) => void;
    setTopBarIcon: (icon: ReactNode | undefined) => void;
    setDisabledAction: (disabled: boolean) => void;
    setDisabledActionTooltip: (tooltip: string | undefined) => void;
}

const MainContainerContext = createContext<MainContainerContextType | undefined>(undefined);

export const MainContainerProvider = ({ children }: { children: ReactNode }) => {
    const [topBarAction, setTopBarAction] = useState<TopBarAction | null>(null);
    const [topBarActionTitle, setTopBarActionTitle] = useState<string | undefined>(undefined);
    const [topBarIcon, setTopBarIcon] = useState<ReactNode | undefined>(undefined);
    const [disabledAction, setDisabledAction] = useState<boolean>(false);
    const [disabledActionTooltip, setDisabledActionTooltip] = useState<string | undefined>(undefined);

    const value = useMemo(() => ({
        topBarAction,
        topBarActionTitle,
        topBarIcon,
        disabledAction,
        disabledActionTooltip,
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
        setDisabledAction,
        setDisabledActionTooltip,
    }), [topBarAction, topBarActionTitle, topBarIcon, disabledAction, disabledActionTooltip]);

    return (
        <MainContainerContext.Provider value={value}>
            {children}
        </MainContainerContext.Provider>
    );
};

export const useMainContainer = () => {
    const context = useContext(MainContainerContext);
    if (!context) {
        throw new Error('useMainContainer has to be used within MainContainerProvider');
    }
    return context;
};
